import { exportToPulser } from '~/systems/experiment/export';
import { assert, assertNever, assertNum } from '~/util';
import type * as schema from '~/models/pulser-schema';
import type * as studioModel from '~/models/studio';

/**
 * Python codegen context
 */

export class Scope {
  scopeVars = new Set();
  constructor(parent: Scope | null = null) {
    if (parent != null) {
      this.scopeVars = new Set(parent.scopeVars);
    }
  }

  var(name: string) {
    if (this.scopeVars.has(name)) {
      let i = 1;
      while (this.scopeVars.has(`${name}_${i}`)) {
        i++;
      }
      name = `${name}_${i}`;
    }
    this.scopeVars.add(name);
    return name;
  }
}

export type CodeMeta =
  { kind: 'pulse'; uuid: string } | { kind: 'atom'; uuid: string };

interface CodeMetaRange {
  meta: CodeMeta;
  start: number;
  end: number;
}

export type CodeLine = {
  content: string;
  metas: CodeMetaRange[];
};

export class Codegen {
  code = '';
  metas: CodeMetaRange[] = [];
  hasLinebreak = false;
  scope = new Scope();

  import(name: string, desiredAlias = name): string {
    const alias = this.scope.var(desiredAlias);
    if (alias === name) {
      this.code += `import ${name}\n`;
    } else {
      this.code += `import ${name} as ${alias}\n`;
    }
    this.hasLinebreak = false;
    return alias;
  }

  importFrom(from: string, ...names: string[]): string[] {
    if (names.length == 0) {
      return [];
    }
    const aliases: string[] = [];
    const importParts: string[] = [];

    for (const name of names) {
      const alias = this.scope.var(name);
      aliases.push(alias);
      if (alias === name) {
        importParts.push(name);
      } else {
        importParts.push(`${name} as ${alias}`);
      }
    }

    this.code += `from ${from} import ${importParts.join(', ')}\n`;
    this.hasLinebreak = false;
    return aliases;
  }

  declareVar(desiredName: string, initExpression: string) {
    const varName = this.scope.var(desiredName);
    this.code += `${varName} = ${initExpression}\n`;
    this.hasLinebreak = false;
    return varName;
  }

  statement(statement: string) {
    this.code += `${statement}\n`;
    this.hasLinebreak = false;
  }

  lineBreak() {
    this.code += '\n';
    this.hasLinebreak = true;
  }

  lineBreakOnce() {
    if (this.hasLinebreak) {
      return;
    }
    this.lineBreak();
  }

  getPos(): number {
    return this.code.length;
  }

  pushMeta(start: number, end: number, meta: CodeMeta) {
    this.metas.push({ meta, start, end });
  }

  rawCode(): string {
    return this.code;
  }

  getCodeLines(): CodeLine[] {
    const rawLines = this.code.split('\n');
    let metasStart = 0;
    let lineStartIdx = 0;
    return rawLines.map((content) => {
      const metas: { meta: CodeMeta; start: number; end: number }[] = [];
      const lineEndIdx = lineStartIdx + content.length;

      while (
        this.metas[metasStart] != null &&
        this.metas[metasStart].end < lineStartIdx
      ) {
        metasStart++;
      }

      for (let i = metasStart; i < this.metas.length; i++) {
        const meta = this.metas[i];
        if (meta.start <= lineEndIdx && meta.end > lineStartIdx) {
          metas.push({
            meta: meta.meta,
            start: meta.start - lineStartIdx,
            end: meta.end - lineStartIdx
          });
        }
      }

      lineStartIdx = lineEndIdx + 1;

      return {
        content,
        metas
      };
    });
  }
}

function getWaveformKinds(
  wf: schema.Waveform,
  ignoreConstants: boolean
): schema.Waveform['kind'][] {
  if (ignoreConstants && wf.kind === 'constant') {
    return [];
  }

  const kinds = [wf.kind];
  if (wf.kind === 'composite') {
    for (const w of wf.waveforms) {
      // do not ignore nested constants
      kinds.push(...getWaveformKinds(w, false));
    }
  }
  return kinds;
}

const wfClassnameMap: Record<schema.Waveform['kind'], string> = {
  constant: 'ConstantWaveform',
  ramp: 'RampWaveform',
  custom: 'CustomWaveform',
  interpolated: 'InterpolatedWaveform',
  blackman: 'BlackmanWaveform',
  blackman_max: 'BlackmanWaveform',
  kaiser: 'KaiserWaveform',
  kaiser_max: 'KaiserWaveform',
  composite: 'CompositeWaveform'
};

/**
 * Converts an experiment to its corresponding Pulser Python code.
 *
 * Returns code as an array of [line + meta data (used by highlighting)]
 */
export function experimentToPulser(
  experiment: studioModel.StudioExperiment
): CodeLine[] {
  const { register: atoms, pulses } = experiment;
  const pulserData = exportToPulser(experiment);

  const gen = new Codegen();
  const atomScope = new Scope();

  const wfUsedTypes = [
    ...new Set(
      pulserData.operations.flatMap((op: schema.Operation) => {
        if (op.op === 'pulse') {
          return [
            ...getWaveformKinds(op.amplitude, true),
            ...getWaveformKinds(op.detuning, true)
          ];
        }
        return [];
      })
    )
  ];
  const wfClassnamesRaw = wfUsedTypes.map((wfType) => wfClassnameMap[wfType]);

  const [varRegister, varSequence, varPulse] = gen.importFrom(
    'pulser',
    'Register',
    'Sequence',
    'Pulse'
  );
  const [varDevice] = gen.importFrom(
    'pulser.devices',
    typeof pulserData.device === 'string'
      ? pulserData.device
      : pulserData.device.name
  );
  const varWaveforms =
    wfClassnamesRaw.length > 0
      ? gen.importFrom('pulser.waveforms', ...wfClassnamesRaw)
      : [];

  const wfClassnames = Object.fromEntries(
    wfUsedTypes.map((wfType, i) => [wfType, varWaveforms[i]])
  );

  function lit(str: string) {
    return JSON.stringify(str);
  }

  const atomMap: string[] = [];
  const atomsDecl = `{\n${pulserData.register
    .map((atom: { name: string; x: number; y: number }) => {
      const uniqueName = atomScope.var(atom.name);
      atomMap.push(uniqueName);
      return `  ${lit(uniqueName)}: (${atom.x}, ${atom.y}),\n`;
    })
    .join('')}}`;

  gen.lineBreakOnce();
  const registerStart = gen.getPos();
  const varReg = gen.declareVar('reg', `${varRegister}(${atomsDecl})`);
  // add metadata for atom lines
  {
    // get last few lines positions
    const appendedCode = gen.rawCode().slice(registerStart);
    const appendedLines = appendedCode.split('\n');

    // first line is just assign
    const assignLine = appendedLines.shift();
    // last two lines are an ending brace and newline, ignore it
    appendedLines.splice(-2, 2);
    assert(assignLine != null);
    assert(appendedLines.length === atoms.length);
    let atomMetaStart = registerStart + assignLine.length + 1;
    for (let idx = 0; idx < appendedLines.length; idx++) {
      const line = appendedLines[idx];
      const atom = atoms[idx];
      assert(atom != null);
      gen.pushMeta(atomMetaStart, atomMetaStart + line.length, {
        kind: 'atom',
        uuid: atom.uuid
      });
      atomMetaStart += line.length + 1;
    }
  }

  gen.lineBreakOnce();
  const varSeq = gen.declareVar(
    'seq',
    `${varSequence}(${varReg}, ${varDevice})`
  );

  for (const [chName, chKind] of Object.entries(pulserData.channels) as [
    string,
    string
  ][]) {
    gen.statement(`${varSeq}.declare_channel(${lit(chName)}, ${lit(chKind)})`);
  }

  gen.lineBreakOnce();

  function declareWaveform(varName: string, wf: schema.Waveform): string {
    const wfClass = wfClassnames[wf.kind];

    switch (wf.kind) {
      case 'constant':
        assertNum(wf.duration);
        assertNum(wf.value);
        return gen.declareVar(varName, `${wfClass}(${wf.duration})`);
      case 'ramp':
        assertNum(wf.duration);
        assertNum(wf.start);
        assertNum(wf.stop);
        return gen.declareVar(
          varName,
          `${wfClass}(${wf.duration}, ${wf.start}, ${wf.stop})`
        );
      case 'blackman':
        assertNum(wf.duration);
        assertNum(wf.area);
        return gen.declareVar(
          varName,
          `${wfClass}(${wf.duration}, ${wf.area})`
        );
      case 'blackman_max':
        assertNum(wf.max_val);
        assertNum(wf.area);
        return gen.declareVar(
          varName,
          `${wfClass}.from_max_val(${wf.max_val}, ${wf.area})`
        );
      case 'kaiser':
        assertNum(wf.duration);
        assertNum(wf.area);
        assertNum(wf.beta);
        return gen.declareVar(
          varName,
          `${wfClass}(${wf.duration}, ${wf.area}, ${wf.beta})`
        );
      case 'kaiser_max':
        assertNum(wf.max_val);
        assertNum(wf.area);
        assertNum(wf.beta);
        return gen.declareVar(
          varName,
          `${wfClass}.from_max_val(${wf.max_val}, ${wf.area}, ${wf.beta})`
        );
      case 'composite': {
        const wfs = wf.waveforms.map((w) => declareWaveform('composite', w));
        return gen.declareVar(varName, `${wfClass}(${wfs.join(', ')})`);
      }
      case 'custom': {
        const varSamples = gen.declareVar(
          'samples',
          JSON.stringify(wf.samples)
        );
        return gen.declareVar(varName, `${wfClass}(${varSamples})`);
      }
      case 'interpolated': {
        assertNum(wf.duration);
        const varValues = gen.declareVar('values', JSON.stringify(wf.values));
        const varTimes = gen.declareVar('times', JSON.stringify(wf.times));
        return gen.declareVar(
          varName,
          `${wfClass}(${wf.duration}, ${varValues}, ${varTimes})`
        );
      }
      default:
        assertNever(wf);
    }
  }

  const pulseVars = new Map<number, string>();
  let pulseIdx = 0;
  // declare pulses upfront
  for (let opIdx = 0; opIdx < pulserData.operations.length; opIdx++) {
    const op = pulserData.operations[opIdx];
    if (op.op === 'pulse') {
      const origPulseUuid = pulses[pulseIdx].uuid;
      pulseIdx++;
      assertNum(op.post_phase_shift);
      const phaseShiftPart =
        op.post_phase_shift === 0 ? '' : `, ${op.post_phase_shift}`;
      const phasePart = `${op.phase}${phaseShiftPart}`;

      gen.lineBreakOnce();
      const pulseStartPos = gen.getPos();
      let pulseDecl;
      if (op.amplitude.kind === 'constant' && op.detuning.kind === 'constant') {
        assertNum(op.amplitude.duration);
        assertNum(op.amplitude.value);
        assertNum(op.detuning.duration);
        assertNum(op.detuning.value);

        const duration = Math.max(op.amplitude.duration, op.detuning.duration);
        pulseDecl = `${varPulse}.ConstantPulse(${duration}, ${op.amplitude.value}, ${op.detuning.value}, ${phasePart})`;
      } else if (op.amplitude.kind === 'constant') {
        assertNum(op.amplitude.value);
        const varDetuning = declareWaveform(`det${pulseIdx}`, op.detuning);
        pulseDecl = `${varPulse}.ConstantAmplitude(${op.amplitude.value}, ${varDetuning}, ${phasePart})`;
      } else if (op.detuning.kind === 'constant') {
        assertNum(op.detuning.value);
        gen.lineBreakOnce();
        const varAmplitude = declareWaveform(`amp${pulseIdx}`, op.amplitude);
        pulseDecl = `${varPulse}.ConstantDetuning(${varAmplitude}, ${op.detuning.value}, ${phasePart})`;
      } else {
        gen.lineBreakOnce();
        const varAmplitude = declareWaveform(`amp${pulseIdx}`, op.amplitude);
        const varDetuning = declareWaveform(`det${pulseIdx}`, op.detuning);
        pulseDecl = `${varPulse}(${varAmplitude}, ${varDetuning}, ${phasePart})`;
      }
      const pulseVar = gen.declareVar(`pulse${pulseIdx}`, pulseDecl);
      gen.pushMeta(pulseStartPos, gen.getPos(), {
        kind: 'pulse',
        uuid: origPulseUuid
      });
      pulseVars.set(opIdx, pulseVar);
    }
  }

  gen.lineBreakOnce();

  pulseIdx = 0;
  for (let opIdx = 0; opIdx < pulserData.operations.length; opIdx++) {
    const op = pulserData.operations[opIdx];
    const opStartPos = gen.getPos();
    let meta: CodeMeta | null = null;
    switch (op.op) {
      case 'pulse': {
        meta = { kind: 'pulse', uuid: pulses[pulseIdx].uuid };
        pulseIdx++;
        gen.statement(
          `${varSeq}.add(${pulseVars.get(opIdx)}, ${lit(op.channel)})`
        );
        break;
      }
      case 'align': {
        gen.statement(`${varSeq}.align(${op.channels.map(lit).join(', ')})`);
        break;
      }
      case 'delay': {
        gen.statement(`${varSeq}.delay(${op.time}, ${lit(op.channel)})`);
        break;
      }
      case 'target': {
        assertNum(op.target);
        meta = { kind: 'atom', uuid: atoms[op.target].uuid };
        gen.statement(
          `${varSeq}.target(${lit(atomMap[op.target])}, ${lit(op.channel)})`
        );
        break;
      }
      case 'phase_shift': {
        gen.statement(
          `${varSeq}.delay(${op.phi}, ${op.targets
            .map((t) => {
              assertNum(t);
              return lit(atomMap[t as number]);
            })
            .join(', ')}, basis=${lit(op.basis)})`
        );
        break;
      }
    }
    if (meta != null) {
      const opEndPos = gen.getPos();
      gen.pushMeta(opStartPos, opEndPos, meta);
    }
  }

  if (pulserData.measurement != null) {
    gen.statement(`${varSeq}.measure(${lit(pulserData.measurement)})`);
  }

  gen.lineBreakOnce();
  gen.statement(`${varSeq}.draw()`);

  return gen.getCodeLines();
}
